import React, { useEffect, useState } from 'react';

import * as JKYModel from '../custom-types';
import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import NumberStepper from './NumberStepper';
import { DIFFICULTY_LABELS, GlobalRank, TriviaAvatar, formatPoints, roundLabel } from './TriviaShared';

import './TriviaHost.scss';

type Difficulty = 'any' | 'easy' | 'medium' | 'hard';

interface Round {
  categoryId: number;
  difficulty: Difficulty;
  questionCount: number;
}

interface Settings {
  mode: 'solo' | 'teams';
  rolling: boolean;
  rounds: Round[];
  answerSeconds: number;
  revealSeconds: number;
  music: { source: 'favorites' | 'playlist' | 'none'; playlistId: number | null };
  schedule: Schedule;
}

interface ScheduleSlot {
  day: number;
  start: string;
  end: string;
}

interface Schedule {
  enabled: boolean;
  timezone: string;
  slots: ScheduleSlot[];
}

interface MusicOption {
  source: 'favorites' | 'playlist' | 'none';
  playlistId: number | null;
  name: string;
  count: number;
}

interface SetupResponse {
  settings: Settings;
  categories: { id: number; name: string }[];
  music: MusicOption[];
  limits: { rounds: { max: number }; questionCount: { min: number; max: number } };
}

// The server's id for "pick a category at random for this round".
const RANDOM_CATEGORY = -1;

const ANSWER_CHOICES = [10, 15, 20, 30, 45, 60, 90, 120];
const REVEAL_CHOICES = [5, 8, 10, 15, 20, 30, 45, 60];

const PHASE_TEXT: { [phase: string]: string } = {
  lobby: 'Waiting for players',
  loading: 'Getting questions',
  roundIntro: 'Round starting',
  question: 'Question on screen',
  reveal: 'Showing the answer',
  standings: 'Rankings on screen',
  final: 'Final scores on screen',
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MAX_SLOTS = 21;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const deviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

const musicKey = (option: { source: string; playlistId: number | null }) =>
  option.source === 'playlist' ? `playlist:${option.playlistId}` : option.source;

interface TriviaHostProps {
  playerIdentifier: string;
  token: string;
  trivia: JKYModel.TriviaState | undefined;
  onNotice(tone: 'ok' | 'bad', text: string): void;
}

/**
 * The host's side of trivia night, in the control panel: run the game that is on, and set
 * up the next one. The setup is saved as it changes and used by every game started after.
 */
const TriviaHost = ({ playerIdentifier, token, trivia, onNotice }: TriviaHostProps) => {
  const [setup, setSetup] = useState<SetupResponse>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string>(null);

  useEffect(() => {
    api
      .trivia(`${playerIdentifier}/settings`, 'GET', token, null)
      .then((response) => setSetup(response))
      .catch((error) => setLoadError(error.message));
  }, [playerIdentifier]);

  const save = async (settings: Settings) => {
    setSetup((current) => current && { ...current, settings });
    try {
      const response = await api.trivia(`${playerIdentifier}/settings`, 'PUT', token, settings);
      trackEvent('trivia_settings_saved', { player_code: playerIdentifier });
      setSetup((current) => current && { ...current, settings: response.settings });
    } catch (error) {
      onNotice('bad', error.message);
    }
  };

  const next = async () => {
    setBusy(true);
    try {
      await api.trivia(`${playerIdentifier}/next`, 'POST', token, {});
      trackEvent('trivia_next', { player_code: playerIdentifier });
    } catch (error) {
      onNotice('bad', error.message);
    } finally {
      setBusy(false);
    }
  };

  const restart = async () => {
    setBusy(true);
    try {
      await api.trivia(`${playerIdentifier}/start`, 'POST', token, {});
      trackEvent('trivia_restarted', { player_code: playerIdentifier });
      onNotice('ok', 'New game ready. Players can join now.');
    } catch (error) {
      onNotice('bad', error.message);
    } finally {
      setBusy(false);
    }
  };

  const active = !!(trivia && trivia.active);
  const settings = setup && setup.settings;

  const updateRound = (index: number, change: Partial<Round>) =>
    save({ ...settings, rounds: settings.rounds.map((r, i) => (i === index ? { ...r, ...change } : r)) });

  return (
    <div className="trivia-host">
      {active && <LiveGame trivia={trivia} busy={busy} onNext={next} onRestart={restart} />}

      {active && !!settings && !!trivia.rolling !== !!settings.rolling && (
        <p className="control-hint">
          The format you picked takes effect when you start a new game, or switch the TV to the jukebox and back.
        </p>
      )}

      {!active && (
        <p className="control-hint">
          Use the switch above to put trivia on the TV. Players scan the code on the screen, join with their Jookable
          account and answer on their phones. Songs in the queue keep playing under the questions.
        </p>
      )}

      <h3 className="trivia-host-heading">{active ? 'Setup for the next game' : 'Game setup'}</h3>
      {!!loadError && <div className="jukebox-notice jukebox-notice-bad">{loadError}</div>}
      {!settings && !loadError && <p className="jukebox-empty">Loading the setup.</p>}

      {!!settings && (
        <div className="control-rules">
          <div className="control-rule">
            <label className="control-rule-label" htmlFor="trivia-format">
              Format
            </label>
            <select
              id="trivia-format"
              className="control-select"
              value={settings.rolling ? 'rolling' : 'night'}
              onChange={(e) => save({ ...settings, rolling: e.target.value === 'rolling' })}
            >
              <option value="night">Trivia night</option>
              <option value="rolling">Rolling, all day</option>
            </select>
            <div className="control-rule-help">
              {settings.rolling
                ? 'Starts right away and never stops. Players join and leave whenever they like. After each round the scores show for a moment, then a new game starts with fresh questions. Anyone who misses three questions in a row drops off the board until they answer again.'
                : 'Players join in a lobby, you start the first question, and the game ends with final scores.'}
            </div>
          </div>

          <div className="control-rule">
            <label className="control-rule-label" htmlFor="trivia-mode">
              Play as
            </label>
            <select
              id="trivia-mode"
              className="control-select"
              value={settings.mode}
              onChange={(e) => save({ ...settings, mode: e.target.value as 'solo' | 'teams' })}
            >
              <option value="solo">Individuals</option>
              <option value="teams">Teams</option>
            </select>
            <div className="control-rule-help">
              {settings.mode === 'teams'
                ? 'Players start or join a team on their phone, and everyone still answers on their own phone. A team scores the average of its players, so a big table has no edge over a small one.'
                : 'Everyone plays for themselves.'}
            </div>
          </div>

          {settings.rolling ? (
            <div className="control-rule">
              <div className="control-rule-label">Questions</div>
              <label className="trivia-host-field">
                <span>Category</span>
                <select
                  className="control-select"
                  value={settings.rounds[0].categoryId}
                  onChange={(e) => updateRound(0, { categoryId: Number(e.target.value) })}
                >
                  {setup.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="trivia-host-field">
                <span>Difficulty</span>
                <select
                  className="control-select"
                  value={settings.rounds[0].difficulty}
                  onChange={(e) => updateRound(0, { difficulty: e.target.value as Difficulty })}
                >
                  {Object.keys(DIFFICULTY_LABELS).map((key) => (
                    <option key={key} value={key}>
                      {DIFFICULTY_LABELS[key]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="trivia-host-field">
                <span>Questions per round</span>
                <div className="control-stepper">
                  <NumberStepper
                    aria-label="Questions per round"
                    value={settings.rounds[0].questionCount}
                    min={setup.limits.questionCount.min}
                    max={setup.limits.questionCount.max}
                    onKeyPress={(e) => e.preventDefault()}
                    onChange={(value) => value != null && updateRound(0, { questionCount: value })}
                  />
                </div>
              </div>
              <div className="control-rule-help">
                A random category is picked again for every round. Any category mixes categories question by question.
              </div>
            </div>
          ) : (
            <div className="control-rule">
              <div className="control-rule-label">Rounds</div>
              <ol className="trivia-host-rounds">
                {settings.rounds.map((round, index) => (
                  <li key={index} className="trivia-host-round">
                    <div className="trivia-host-round-top">
                      <span className="trivia-host-round-number">Round {index + 1}</span>
                      {settings.rounds.length > 1 && (
                        <button
                          type="button"
                          className="jukebox-action"
                          onClick={() => save({ ...settings, rounds: settings.rounds.filter((_, i) => i !== index) })}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <label className="trivia-host-field">
                      <span>Category</span>
                      <select
                        className="control-select"
                        value={round.categoryId}
                        onChange={(e) => updateRound(index, { categoryId: Number(e.target.value) })}
                      >
                        {setup.categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="trivia-host-field">
                      <span>Difficulty</span>
                      <select
                        className="control-select"
                        value={round.difficulty}
                        onChange={(e) => updateRound(index, { difficulty: e.target.value as Difficulty })}
                      >
                        {Object.keys(DIFFICULTY_LABELS).map((key) => (
                          <option key={key} value={key}>
                            {DIFFICULTY_LABELS[key]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="trivia-host-field">
                      <span>Questions</span>
                      <div className="control-stepper">
                        <NumberStepper
                          aria-label={`Questions in round ${index + 1}`}
                          value={round.questionCount}
                          min={setup.limits.questionCount.min}
                          max={setup.limits.questionCount.max}
                          onKeyPress={(e) => e.preventDefault()}
                          onChange={(value) => value != null && updateRound(index, { questionCount: value })}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              {settings.rounds.some((round) => round.categoryId !== RANDOM_CATEGORY) && (
                <button
                  type="button"
                  className="trivia-host-add"
                  onClick={() =>
                    save({ ...settings, rounds: settings.rounds.map((r) => ({ ...r, categoryId: RANDOM_CATEGORY })) })
                  }
                >
                  Random category for every round
                </button>
              )}
              {settings.rounds.length < setup.limits.rounds.max && (
                <button
                  type="button"
                  className="trivia-host-add"
                  onClick={() =>
                    save({
                      ...settings,
                      rounds: [...settings.rounds, { categoryId: 0, difficulty: 'any', questionCount: 10 }],
                    })
                  }
                >
                  Add a round
                </button>
              )}
              <div className="control-rule-help">
                Each game draws fresh questions, and a venue does not get the same question twice until it has been
                through every one that fits. A random category is picked again for every game, and you still choose its
                difficulty. Any category mixes categories question by question.
              </div>
            </div>
          )}

          <div className="control-rule">
            <label className="control-rule-label" htmlFor="trivia-answer-seconds">
              Time to answer
            </label>
            <select
              id="trivia-answer-seconds"
              className="control-select"
              value={settings.answerSeconds}
              onChange={(e) => save({ ...settings, answerSeconds: Number(e.target.value) })}
            >
              {withCurrent(ANSWER_CHOICES, settings.answerSeconds).map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds} seconds
                </option>
              ))}
            </select>
            <div className="control-rule-help">How long each question stays up before the answer shows.</div>
          </div>

          <div className="control-rule">
            <label className="control-rule-label" htmlFor="trivia-reveal-seconds">
              Answer and leaderboard
            </label>
            <select
              id="trivia-reveal-seconds"
              className="control-select"
              value={settings.revealSeconds}
              onChange={(e) => save({ ...settings, revealSeconds: Number(e.target.value) })}
            >
              {withCurrent(REVEAL_CHOICES, settings.revealSeconds).map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds} seconds
                </option>
              ))}
            </select>
            <div className="control-rule-help">
              How long the answer and the leader stay up before the next question.
            </div>
          </div>

          <div className="control-rule">
            <label className="control-rule-label" htmlFor="trivia-music">
              Music during trivia
            </label>
            <select
              id="trivia-music"
              className="control-select"
              value={musicKey(settings.music)}
              onChange={(e) => {
                const option = setup.music.find((o) => musicKey(o) === e.target.value);
                if (option) save({ ...settings, music: { source: option.source, playlistId: option.playlistId } });
              }}
            >
              {setup.music.map((option) => (
                <option key={musicKey(option)} value={musicKey(option)}>
                  {option.source === 'none' ? option.name : `${option.name} (${option.count} songs)`}
                </option>
              ))}
            </select>
            <div className="control-rule-help">
              Plays under the questions whenever nobody has a song in the queue. Songs patrons add play first. Star
              songs on the jukebox screen to add them to your favorites.
            </div>
          </div>

          <TriviaSchedule
            schedule={settings.schedule}
            onChange={(schedule) => save({ ...settings, schedule: { ...schedule, timezone: deviceTimeZone() } })}
          />
        </div>
      )}
    </div>
  );
};

const withCurrent = (choices: number[], current: number) =>
  choices.indexOf(current) >= 0 ? choices : [...choices, current].sort((a, b) => a - b);

interface LiveGameProps {
  trivia: JKYModel.TriviaState;
  busy: boolean;
  onNext(): void;
  onRestart(): void;
}

const LiveGame = ({ trivia, busy, onNext, onRestart }: LiveGameProps) => {
  const players = trivia.players || [];
  const round = (trivia.rounds || [])[trivia.roundIndex || 0];
  const phase = trivia.phase;
  const rolling = !!trivia.rolling;

  let action: React.ReactNode = null;
  if (rolling && phase === 'final') {
    action = (
      <button type="button" className="jukebox-action trivia-host-skip" disabled={busy} onClick={onNext}>
        Start the next round now
      </button>
    );
  } else if (phase === 'lobby') {
    action = (
      <button type="button" className="trivia-host-go" disabled={busy || !trivia.firstRoundReady} onClick={onNext}>
        {trivia.firstRoundReady ? 'Start the first question' : 'Getting questions…'}
      </button>
    );
  } else if (phase === 'final') {
    action = (
      <button type="button" className="trivia-host-go" disabled={busy} onClick={onRestart}>
        Start a new game
      </button>
    );
  } else if (phase !== 'loading') {
    action = (
      <button type="button" className="jukebox-action trivia-host-skip" disabled={busy} onClick={onNext}>
        {phase === 'question'
          ? 'Show the answer now'
          : phase === 'reveal'
            ? 'Next question now'
            : phase === 'standings'
              ? 'Start the next round now'
              : 'Start now'}
      </button>
    );
  }

  return (
    <div className="trivia-host-live">
      <div className="trivia-host-status">
        <span className="trivia-host-dot" />
        {rolling
          ? `Rolling · Round ${trivia.lap || 1} · ${phase === 'final' ? 'Rankings on screen' : PHASE_TEXT[phase]}`
          : PHASE_TEXT[phase] || 'Trivia is on'}
      </div>
      {phase !== 'lobby' && phase !== 'final' && (
        <div className="trivia-host-where">
          {rolling
            ? roundLabel(round)
            : `Round ${(trivia.roundIndex || 0) + 1} of ${trivia.roundCount} · ${roundLabel(round)}`}
          {(phase === 'question' || phase === 'reveal') &&
            ` · Question ${(trivia.questionIndex || 0) + 1} of ${trivia.questionCount}`}
        </div>
      )}
      {!!trivia.question && <div className="trivia-host-question">{trivia.question.text}</div>}
      {phase === 'question' && (
        <div className="trivia-host-where">
          {trivia.answeredCount} of {trivia.playingCount != null ? trivia.playingCount : players.length} answered
        </div>
      )}
      {!!trivia.error && <div className="jukebox-notice jukebox-notice-bad">{trivia.error}</div>}
      {action}

      {trivia.mode === 'teams' ? (
        <div className="trivia-host-standings">
          <div className="control-rule-label">
            {(trivia.teams || []).length} {(trivia.teams || []).length === 1 ? 'team' : 'teams'}
          </div>
          <ol>
            {(trivia.teams || []).slice(0, 5).map((team) => (
              <li key={team.id}>
                <span className="trivia-host-rank">{team.rank}</span>
                <span className="trivia-host-name">
                  {team.name} <span className="trivia-host-members">· {team.members.length}</span>
                </span>
                <span className="trivia-host-score">{formatPoints(team.score)}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="trivia-host-standings">
          <div className="control-rule-label">
            {players.length} {players.length === 1 ? 'player' : 'players'}
          </div>
          <ol>
            {players.slice(0, 5).map((p) => (
              <li key={p.id}>
                <span className="trivia-host-rank">{p.rank}</span>
                <TriviaAvatar person={p} className="trivia-host-avatar" />
                <span className="trivia-host-name">
                  {p.username} <GlobalRank person={p} />
                </span>
                <span className="trivia-host-score">{formatPoints(p.score)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default TriviaHost;

interface TriviaScheduleProps {
  schedule: Schedule;
  onChange(schedule: Schedule): void;
}

/**
 * Weekly times when the TV switches to trivia by itself and opens the lobby, then back to
 * the jukebox at the end. Times are the venue's, taken from this device's time zone.
 */
const TriviaSchedule = ({ schedule, onChange }: TriviaScheduleProps) => {
  const current = schedule || { enabled: false, timezone: deviceTimeZone(), slots: [] };
  const slots = current.slots;
  const setSlot = (index: number, change: Partial<ScheduleSlot>) => {
    const slot = { ...slots[index], ...change };
    // A time box that is half typed or cleared is not saved; the server would drop it.
    if (!TIME.test(slot.start) || !TIME.test(slot.end) || slot.start === slot.end) {
      return;
    }
    onChange({ ...current, slots: slots.map((s, i) => (i === index ? slot : s)) });
  };
  const addSlot = () => {
    const last = slots[slots.length - 1];
    const slot = last ? { ...last, day: (last.day + 1) % 7 } : { day: 5, start: '20:00', end: '22:00' };
    onChange({ ...current, enabled: true, slots: [...slots, slot] });
  };

  return (
    <div className="control-rule trivia-schedule">
      <button
        type="button"
        role="switch"
        aria-checked={current.enabled}
        className={current.enabled ? 'control-switch is-on' : 'control-switch'}
        onClick={() => onChange({ ...current, enabled: !current.enabled })}
      >
        <span className="control-switch-track" aria-hidden="true">
          <span className="control-switch-knob" />
        </span>
        <span className="control-rule-label">Trivia on a schedule</span>
        <span className="control-switch-state">{current.enabled ? 'On' : 'Off'}</span>
      </button>
      <div className="control-rule-help">
        At each start time the TV switches to trivia and opens the lobby. At the end time it goes back to music only.
        The switch above still works, and stays how you left it until the next start or end.
      </div>

      {current.enabled && (
        <>
          {slots.length === 0 && <p className="jukebox-empty">No times yet.</p>}
          {slots.map((slot, index) => (
            <div className="trivia-schedule-slot" key={index}>
              <div className="trivia-schedule-day">
                <select
                  className="control-select"
                  aria-label="Day"
                  value={slot.day}
                  onChange={(e) => setSlot(index, { day: Number(e.target.value) })}
                >
                  {DAY_NAMES.map((name, day) => (
                    <option key={name} value={day}>
                      {name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="jukebox-action"
                  onClick={() => onChange({ ...current, slots: slots.filter((_, i) => i !== index) })}
                >
                  Remove
                </button>
              </div>
              <div className="trivia-schedule-times">
                <input
                  type="time"
                  className="control-select"
                  aria-label="Start"
                  value={slot.start}
                  onChange={(e) => setSlot(index, { start: e.target.value })}
                />
                <span>to</span>
                <input
                  type="time"
                  className="control-select"
                  aria-label="End"
                  value={slot.end}
                  onChange={(e) => setSlot(index, { end: e.target.value })}
                />
              </div>
            </div>
          ))}
          {slots.length < MAX_SLOTS && (
            <button type="button" className="jukebox-action trivia-schedule-add" onClick={addSlot}>
              Add a time
            </button>
          )}
          <div className="control-rule-help">
            Times are in {current.timezone || deviceTimeZone()}. An end time earlier than the start runs past midnight.
          </div>
        </>
      )}
    </div>
  );
};
